// src/pages/CadastrarSIP.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function CadastrarSIP() {
  const [assistentes, setAssistentes] = useState([]);
  const [form, setForm] = useState({
    nome: '',
    usuario: '',
    servidor: '',
    senha: '', // Adicionando o campo senha que faltava na lógica, mas é crucial para um SIP
    porta: '5060',
    protocolo: 'UDP',
    inboundEnabled: true,
    outboundEnabled: true,
    techPrefix: '',
    phoneNumberBody: {
      number: '',
      name: '',
      assistantId: '',
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net';
  // const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/';


  // Busca os assistentes disponíveis para popular o dropdown
  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/assistentes/master`);
        setAssistentes(response.data);
      } catch (err) {
        console.error("Erro ao buscar assistentes", err);
        toast.error('Não foi possível carregar os assistentes.');
      }
    };
    fetchAssistants();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    // Lógica para atualizar campos aninhados (phoneNumberBody) e campos de nível superior
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setForm(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: finalValue }
      }));
    } else {
      setForm(prev => ({ ...prev, [name]: finalValue }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const loadingToast = toast.loading('Cadastrando SIP...');

    try {
      // O endpoint original parecia esperar a senha no body principal
      // Ajustando o payload para corresponder à API de criação de SIP
      const payload = {
        nome: form.nome,
        usuario: form.usuario,
        servidor: form.servidor,
        senha: form.senha,
        porta: form.porta,
        protocolo: form.protocolo,
        inboundEnabled: form.inboundEnabled,
        outboundEnabled: form.outboundEnabled,
        techPrefix: form.techPrefix,
        phoneNumberBody: {
          ...form.phoneNumberBody,
          provider: 'bye-phone-number' // Este valor parece ser fixo
        }
      };

      await axios.post(`${baseUrl}/api/sips`, payload);
      toast.success('SIP cadastrado com sucesso!', { id: loadingToast });
      navigate('/sips'); // Redireciona para a lista de SIPs após o sucesso
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || 'Erro ao cadastrar SIP.';
      toast.error(errorMessage, { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClassName =
    "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <div className="space-y-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Cadastrar Novo SIP</h1>
            <p className="text-sm text-muted-foreground">Configure credenciais SIP e associe um número.</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">

          {/* Seção de Credenciais SIP */}
          <fieldset className="rounded-lg border border-border p-6">
            <legend className="px-2 text-lg font-semibold text-primary">Credenciais do SIP</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="nome">Nome de Identificação</Label>
                <Input id="nome" name="nome" type="text" value={form.nome} onChange={handleChange} className="mt-1" required placeholder="Ex: Ramal Vendas 01" />
              </div>
              <div>
                <Label htmlFor="usuario">Usuário SIP</Label>
                <Input id="usuario" name="usuario" type="text" value={form.usuario} onChange={handleChange} className="mt-1" required placeholder="Ex: 1001" />
              </div>
              <div>
                <Label htmlFor="servidor">Servidor/Host</Label>
                <Input id="servidor" name="servidor" type="text" value={form.servidor} onChange={handleChange} className="mt-1" required placeholder="sip.seuprovedor.com" />
              </div>
              <div>
                <Label htmlFor="senha">Senha</Label>
                <Input id="senha" name="senha" type="password" value={form.senha} onChange={handleChange} className="mt-1" required />
              </div>
              <div>
                <Label htmlFor="porta">Porta</Label>
                <Input id="porta" name="porta" type="text" value={form.porta} onChange={handleChange} className="mt-1" required />
              </div>
              <div>
                <Label htmlFor="protocolo">Outbound Protocol</Label>
                <select id="protocolo" name="protocolo" value={form.protocolo} onChange={handleChange} className={`${selectClassName} mt-1`}>
                  <option value="UDP">UDP</option>
                  <option value="TCP">TCP</option>
                  <option value="TLS">TLS</option>
                  <option value="TLS/SRTP">TLS/SRTP</option>
                </select>
              </div>
              <div>
                <Label htmlFor="techPrefix">Tech Prefix (opcional)</Label>
                <Input id="techPrefix" name="techPrefix" type="text" value={form.techPrefix} onChange={handleChange} className="mt-1" placeholder="Ex: 555" />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Direção das chamadas</Label>
                <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      name="inboundEnabled"
                      checked={!!form.inboundEnabled}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    Permitir chamadas de entrada
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      name="outboundEnabled"
                      checked={!!form.outboundEnabled}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                    />
                    Permitir chamadas de saída
                  </label>
                </div>
              </div>
            </div>
          </fieldset>

          {/* Seção do Número de Telefone */}
          <fieldset className="rounded-lg border border-border p-6">
            <legend className="px-2 text-lg font-semibold text-primary">Número de Telefone Associado</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phoneNumberBody.number">Número (E.164)</Label>
                <Input id="phoneNumberBody.number" name="phoneNumberBody.number" type="text" value={form.phoneNumberBody.number} onChange={handleChange} className="mt-1" required placeholder="+5511999998888" />
              </div>
              <div>
                <Label htmlFor="phoneNumberBody.name">Nome de Exibição</Label>
                <Input id="phoneNumberBody.name" name="phoneNumberBody.name" type="text" value={form.phoneNumberBody.name} onChange={handleChange} className="mt-1" required placeholder="Nome que aparece no bina" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="phoneNumberBody.assistantId">Assistente de Voz</Label>
                <select id="phoneNumberBody.assistantId" name="phoneNumberBody.assistantId" value={form.phoneNumberBody.assistantId} onChange={handleChange} className={`${selectClassName} mt-1`} required>
                  <option value="">Selecione um assistente</option>
                  {assistentes.map(a => (
                    <option key={a.vapiId} value={a.vapiId}>{a.nome || a.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>

          {/* Botão de Submissão */}
          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={isSubmitting}>
              <LockClosedIcon className="h-5 w-5" />
              {isSubmitting ? 'Cadastrando...' : 'Cadastrar SIP e Número'}
            </Button>
          </div>
        </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}